// Side-effect import: ships the compact-tables stylesheet on every chat
// surface that uses this hook. The :global selectors only match when the
// COMPACT_TABLES_CLASS marker is applied at a chat call site.
// eslint-disable-next-line primer-react/enforce-css-module-default-import
import '../components/ChatMessageTable.module.css'

import {announce, announceFromElement} from '@github-ui/aria-live'
import {useGetChatState} from '@github-ui/copilot-chat-contexts/ObservableChatContext'
import {findAuthor, isAgent} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {capitalize} from '@github-ui/copilot-chat-helpers/string'
import {getSelectedThread} from '@github-ui/copilot-chat-state/get-selected-thread'
import type {CopilotChatModel, CopilotClientConfirmation, SkillExecution} from '@github-ui/copilot-chat-types'
import type {MarkdownRendererProps} from '@github-ui/copilot-markdown'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useChatMessage} from '../components/ChatMessageContext'
import tableBadgesExtension from '../markdown-extensions/table-badges/table-badges'
import {useChatStateValues} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {useActivePlugin} from './use-active-plugin'

// Hoist to module scope so the extension's `reactComponents` reference is
// stable across renders. `useCombinedComponents` in `@github-ui/copilot-markdown`
// throws if any extension's `reactComponents` object changes identity.
const TABLE_BADGES_EXTENSION = tableBadgesExtension()

export function useChatMessageBehavior({
  excludeFeedback,
  inputRef,
  isLatestMessage,
  isStreaming,
  removeOutdatedContentPreviewItems,
}: {
  excludeFeedback?: boolean
  inputRef?: React.RefObject<HTMLInputElement | null> | React.RefObject<HTMLTextAreaElement | null>
  isLatestMessage?: boolean
  isSharedMessage?: boolean
  isStreaming?: boolean
  excludeReferencesFromFocusZone?: boolean
  removeOutdatedContentPreviewItems?: () => void
}) {
  const {message} = useChatMessage()
  const state = useChatStateValues('currentUserLogin', 'mode')
  const getChatState = useGetChatState()
  const manager = useChatManager()
  const plugin = useActivePlugin()

  const author = findAuthor(message, state.currentUserLogin)
  const isCopilot = author.name === 'Copilot'
  const isUser = author.type === 'user'
  const isAI = isCopilot || isAgent(author)
  const isAgentError = isCopilot && !!message.agentErrors?.length
  const isError = !isAgentError && isCopilot && message.error ? message.error.isError : false
  const renderFeedback = isCopilot && !excludeFeedback && (!isLatestMessage || !isStreaming) && !isError
  const myRef = useRef<HTMLDivElement>(null)
  const isInterrupted = message.interrupted
  const hasClientConfirmations = message.clientConfirmations && message.clientConfirmations.length > 0

  const isErrorRetryable = isError && message.error?.retryable
  const [shownSkillExecutionsForRespondingText, setShownSkillExecutionsForRespondingText] = useState<SkillExecution[]>(
    [],
  )
  const [skillExecutionToUseForRespondingText, setSkillExecutionToUseForRespondingText] = useState<
    SkillExecution | undefined
  >(undefined)
  const showRetryButton = (isErrorRetryable || isInterrupted) && isLatestMessage
  const isLatestStreamingMessage = isLatestMessage && isStreaming

  const pluginMarkdownExtensions = plugin?.markdownExtensions

  const rendererConfig: Omit<MarkdownRendererProps, 'markdown'> = useMemo(() => {
    return {
      openLinksInCurrentTab: state.mode === 'assistive',
      isStreaming: isLatestStreamingMessage,
      extensions: [
        TABLE_BADGES_EXTENSION,
        ...(pluginMarkdownExtensions ? pluginMarkdownExtensions.map(extension => extension()) : []),
      ],
    }
  }, [state.mode, isLatestStreamingMessage, pluginMarkdownExtensions])

  const shouldShowMessageActions =
    (!isError &&
      (renderFeedback || (isAI && !!message.content && !isStreaming)) &&
      (!isUser || !message.confirmations)) ||
    (isUser && !isLatestMessage) ||
    (isError && isLatestMessage)

  const activeSkillAnimationTime = 2000
  const lastSkillUpdateTime = useRef<number>(0)

  useEffect(() => {
    // If the message changes, such as when the user edits their message, reset the shown skill executions
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSkillExecutionToUseForRespondingText(undefined)
    setShownSkillExecutionsForRespondingText([])
  }, [message.id])

  useEffect(() => {
    const skillExecutionToUse = (message.skillExecutions ?? []).find(
      skillExecution =>
        !shownSkillExecutionsForRespondingText.some((shownSkillExecution: SkillExecution) => {
          const hasCallId = shownSkillExecution.callId !== undefined && skillExecution.callId !== undefined

          return (
            (!hasCallId || shownSkillExecution.callId === skillExecution.callId) &&
            shownSkillExecution.slug === skillExecution.slug &&
            shownSkillExecution.statusMessage === skillExecution.statusMessage
          )
        }),
    )

    if (skillExecutionToUse) {
      const timeElapsedSinceLastUpdate = Date.now() - lastSkillUpdateTime.current
      const timeToWait = Math.max(0, activeSkillAnimationTime - timeElapsedSinceLastUpdate)
      const timer = setTimeout(() => {
        lastSkillUpdateTime.current = Date.now()
        setShownSkillExecutionsForRespondingText(prev => [...prev, skillExecutionToUse])
        setSkillExecutionToUseForRespondingText(skillExecutionToUse)
      }, timeToWait)
      return () => clearTimeout(timer)
    }
  }, [message.skillExecutions, shownSkillExecutionsForRespondingText])

  useEffect(() => {
    if (isLatestStreamingMessage) {
      announce('Copilot is responding')
    }
  }, [isLatestStreamingMessage])

  useEffect(() => {
    if (isLatestMessage && !isStreaming && myRef.current) {
      announceFromElement(myRef.current)
    }
  }, [isLatestMessage, isStreaming])

  const handleConfirmationAction = useCallback(
    async (
      clientConfirmation: CopilotClientConfirmation,
      confirmationTitle: string,
      onSubmit?: (accepted: boolean) => void,
    ) => {
      if (onSubmit) {
        onSubmit(clientConfirmation.state === 'accepted')
      } else {
        const localState = getChatState()
        await manager.sendChatMessage({
          thread: getSelectedThread(localState),
          content: `@${author.name} ${capitalize(clientConfirmation.state)} Confirmation: ${confirmationTitle}`,
          references: message.references ?? [],
          topic: localState.currentTopic,
          context: localState.context,
          confirmations: clientConfirmation,
          customInstructions: localState.customInstructions,
          model: localState.model,
        })
      }
    },
    [getChatState, manager, author.name, message.references],
  )

  const focusChatInput = useCallback(() => {
    // setTimeout to delay so we pull focus after ModelPicker's ActionMenu
    // tries to return focus to its (now nonexistent) anchor
    window.setTimeout(() => inputRef?.current?.focus(), 1)
  }, [inputRef])

  const handleRetryErrorMessage = useCallback(async () => {
    const localState = getChatState()
    const thread = getSelectedThread(localState)
    if (!thread) return

    removeOutdatedContentPreviewItems?.()
    focusChatInput()

    sendEvent('dotcom_chat.activate', {target: 'RESPONSE_ACTION_RETRY', mode: localState.mode})
    await manager.retryLastUnsuccessfulChatMessage(thread)
  }, [getChatState, removeOutdatedContentPreviewItems, focusChatInput, manager])

  const handleRetryCopilotResponse = useCallback(
    async (model?: CopilotChatModel) => {
      const localState = getChatState()
      const thread = getSelectedThread(localState)
      if (!thread) return

      removeOutdatedContentPreviewItems?.()
      focusChatInput()

      sendEvent('dotcom_chat.activate', {
        target: 'RESPONSE_ACTION_RETRY_FOR_NEW_SUBTHREAD',
        mode: localState.mode,
      })
      await manager.retryUserChatMessage(thread, message, model)
    },
    [getChatState, manager, message, removeOutdatedContentPreviewItems, focusChatInput],
  )

  const handleEditUserMessage = useCallback(
    async (editedValue: string) => {
      const localState = getChatState()
      const thread = getSelectedThread(localState)
      if (!thread) return

      removeOutdatedContentPreviewItems?.()

      await manager.editUserChatMessage(thread, message, editedValue)
    },
    [getChatState, manager, message, removeOutdatedContentPreviewItems],
  )

  return {
    author,
    removeOutdatedContentPreviewItems,
    handleConfirmationAction,
    handleRetryErrorMessage,
    handleRetryCopilotResponse,
    handleEditUserMessage,
    hasClientConfirmations,
    isCopilot,
    isUser,
    isAI,
    isAgentError,
    isError,
    isInterrupted,
    myRef,
    rendererConfig,
    renderFeedback,
    shouldShowMessageActions,
    skillExecutionToUseForRespondingText,
    showRetryButton,
  }
}
